import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useCompany } from "@/contexts/CompanyContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload,
  FileText,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function HistoricalTax() {
  const { user } = useAuth();
  const { selectedCompanyId, setSelectedCompanyId } = useCompany();
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);

  // Fetch companies
  const { data: companies } = trpc.company.list.useQuery();

  // Fetch historical data
  const { data: taxFilings, refetch: refetchFilings } =
    trpc.historicalTax.listTaxFilings.useQuery(
      { companyId: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  const { data: financialStatements, refetch: refetchStatements } =
    trpc.historicalTax.listFinancialStatements.useQuery(
      { companyId: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  const { data: taxRulings, refetch: refetchRulings } =
    trpc.historicalTax.listTaxRulings.useQuery(
      { companyId: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  const uploadMutation = trpc.historicalTax.uploadDocument.useMutation({
    onSuccess: data => {
      setUploadResult(data);
      setUploading(false);
      // Refetch all lists
      refetchFilings();
      refetchStatements();
      refetchRulings();
    },
    onError: error => {
      console.error("Upload error:", error);
      setUploading(false);
      setUploadResult({ error: error.message });
    },
  });

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !selectedCompanyId) return;

    if (!file.type.includes("pdf")) {
      setUploadResult({ error: "Only PDF files are supported" });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      // Upload to S3 first
      const formData = new FormData();
      formData.append("file", file);
      formData.append("companyId", selectedCompanyId.toString());

      const uploadResponse = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      const { url } = await uploadResponse.json();

      // Process document with LLM
      await uploadMutation.mutateAsync({
        companyId: selectedCompanyId,
        s3Url: url,
      });
    } catch (error) {
      console.error("Upload error:", error);
      setUploadResult({
        error: error instanceof Error ? error.message : "Upload failed",
      });
      setUploading(false);
    }
  };

  const formatCurrency = (cents: number | null) => {
    if (cents === null) return "-";
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(cents / 100);
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("de-CH");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Historical Tax Documents</h1>
          <p className="text-muted-foreground mt-2">
            Upload and manage historical tax filings, financial statements, and
            rulings for year-over-year continuity
          </p>
        </div>

        {/* Company Selector */}
        <Card>
          <CardHeader>
            <CardTitle>Select Company</CardTitle>
            <CardDescription>
              Choose a company to view or upload historical documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {companies?.map(company => (
                <Button
                  key={company.id}
                  variant={
                    selectedCompanyId === company.id ? "default" : "outline"
                  }
                  onClick={() => setSelectedCompanyId(company.id)}
                  className="justify-start h-auto p-4"
                >
                  <div className="text-left">
                    <div className="font-semibold">{company.name}</div>
                    {company.legalForm && (
                      <div className="text-xs text-muted-foreground">
                        {company.legalForm}
                      </div>
                    )}
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {selectedCompanyId && (
          <>
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Upload Tax Document
                </CardTitle>
                <CardDescription>
                  Upload PDF tax filings, financial statements, or rulings. The
                  system will automatically classify and extract data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    id="tax-doc-upload"
                  />
                  <label
                    htmlFor="tax-doc-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <FileText className="h-12 w-12 text-muted-foreground" />
                    <div className="text-sm font-medium">
                      {uploading ? "Processing..." : "Click to upload PDF"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Supports tax filings, balance sheets, income statements,
                      and rulings
                    </div>
                  </label>
                </div>

                {uploadResult && (
                  <Alert
                    variant={uploadResult.error ? "destructive" : "default"}
                  >
                    {uploadResult.error ? (
                      <AlertCircle className="h-4 w-4" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    <AlertDescription>
                      {uploadResult.error ? (
                        uploadResult.error
                      ) : (
                        <div className="space-y-2">
                          <div className="font-semibold">
                            Document processed successfully!
                          </div>
                          <div className="text-sm space-y-1">
                            <div>
                              Type: <Badge>{uploadResult.documentType}</Badge>
                            </div>
                            <div>Confidence: {uploadResult.confidence}%</div>
                            <div>Pages: {uploadResult.pageCount}</div>
                            <div>
                              Fiscal Year:{" "}
                              {uploadResult.extractedData?.fiscalYear}
                            </div>
                          </div>
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Timeline View */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Historical Timeline
                </CardTitle>
                <CardDescription>
                  Year-by-year view of tax filings, financial statements, and
                  rulings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="filings">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="filings">
                      Tax Filings ({taxFilings?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="statements">
                      Financial Statements ({financialStatements?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="rulings">
                      Tax Rulings ({taxRulings?.length || 0})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="filings" className="space-y-4 mt-4">
                    {taxFilings && taxFilings.length > 0 ? (
                      taxFilings.map(filing => (
                        <Card key={filing.id}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-lg">
                                  {filing.fiscalYear} -{" "}
                                  {filing.filingType
                                    .replace(/_/g, " ")
                                    .toUpperCase()}
                                </CardTitle>
                                <CardDescription>
                                  {filing.canton && `Canton: ${filing.canton}`}
                                </CardDescription>
                              </div>
                              <Badge
                                variant={
                                  filing.status === "closed"
                                    ? "default"
                                    : "secondary"
                                }
                              >
                                {filing.status}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <div className="text-muted-foreground">
                                  Revenue
                                </div>
                                <div className="font-semibold">
                                  {formatCurrency(filing.revenue)}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">
                                  Net Income
                                </div>
                                <div className="font-semibold">
                                  {formatCurrency(filing.netIncome)}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">
                                  Tax Amount
                                </div>
                                <div className="font-semibold">
                                  {formatCurrency(filing.taxAmount)}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">
                                  Submitted
                                </div>
                                <div className="font-semibold">
                                  {formatDate(filing.submittedDate)}
                                </div>
                              </div>
                            </div>
                            {filing.extractionConfidence &&
                              filing.extractionConfidence < 80 && (
                                <Alert className="mt-4">
                                  <Clock className="h-4 w-4" />
                                  <AlertDescription>
                                    Low confidence extraction (
                                    {filing.extractionConfidence}%). Review
                                    recommended.
                                  </AlertDescription>
                                </Alert>
                              )}
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No tax filings uploaded yet
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="statements" className="space-y-4 mt-4">
                    {financialStatements && financialStatements.length > 0 ? (
                      financialStatements.map(stmt => (
                        <Card key={stmt.id}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-lg">
                                  {stmt.fiscalYear} -{" "}
                                  {stmt.statementType
                                    .replace(/_/g, " ")
                                    .toUpperCase()}
                                </CardTitle>
                                <CardDescription>
                                  Period: {stmt.period}
                                </CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              {stmt.statementType === "balance_sheet" && (
                                <>
                                  <div>
                                    <div className="text-muted-foreground">
                                      Total Assets
                                    </div>
                                    <div className="font-semibold">
                                      {formatCurrency(stmt.totalAssets)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-muted-foreground">
                                      Total Liabilities
                                    </div>
                                    <div className="font-semibold">
                                      {formatCurrency(stmt.totalLiabilities)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-muted-foreground">
                                      Total Equity
                                    </div>
                                    <div className="font-semibold">
                                      {formatCurrency(stmt.totalEquity)}
                                    </div>
                                  </div>
                                </>
                              )}
                              {stmt.statementType === "income_statement" && (
                                <>
                                  <div>
                                    <div className="text-muted-foreground">
                                      Revenue
                                    </div>
                                    <div className="font-semibold">
                                      {formatCurrency(stmt.revenueAmount)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-muted-foreground">
                                      EBIT
                                    </div>
                                    <div className="font-semibold">
                                      {formatCurrency(stmt.ebit)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-muted-foreground">
                                      Net Income
                                    </div>
                                    <div className="font-semibold">
                                      {formatCurrency(stmt.netIncomeAmount)}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No financial statements uploaded yet
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="rulings" className="space-y-4 mt-4">
                    {taxRulings && taxRulings.length > 0 ? (
                      taxRulings.map(ruling => (
                        <Card key={ruling.id}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-lg">
                                  {ruling.fiscalYear} -{" "}
                                  {ruling.rulingType
                                    .replace(/_/g, " ")
                                    .toUpperCase()}
                                </CardTitle>
                                <CardDescription>
                                  {ruling.authority}{" "}
                                  {ruling.canton && `(${ruling.canton})`}
                                  {ruling.referenceNumber &&
                                    ` - Ref: ${ruling.referenceNumber}`}
                                </CardDescription>
                              </div>
                              <Badge
                                variant={
                                  ruling.status === "closed"
                                    ? "default"
                                    : "secondary"
                                }
                              >
                                {ruling.status}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <div className="text-muted-foreground">
                                  Assessed Amount
                                </div>
                                <div className="font-semibold">
                                  {formatCurrency(ruling.assessedAmount)}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">
                                  Paid Amount
                                </div>
                                <div className="font-semibold">
                                  {formatCurrency(ruling.paidAmount)}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">
                                  Outstanding
                                </div>
                                <div className="font-semibold">
                                  {formatCurrency(ruling.outstandingAmount)}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted-foreground">
                                  Issued
                                </div>
                                <div className="font-semibold">
                                  {formatDate(ruling.issuedDate)}
                                </div>
                              </div>
                            </div>
                            {ruling.summary && (
                              <div className="mt-4 p-3 bg-muted rounded-md text-sm">
                                {ruling.summary}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No tax rulings uploaded yet
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
